/**
 * Reveals the custom tailoring measurement fields only while a made-to-measure
 * option value is selected.
 *
 * While hidden the inputs are disabled and cleared, so FormData skips them and no
 * stray line item properties reach the cart. `required` is only applied while
 * visible, so a standard size purchase can never be blocked by empty fields.
 */
(function () {
  'use strict';

  // Easify injects a second copy of these fields and binds Add to cart. Strip
  // that copy's inputs so they cannot submit, even if the app script still runs.
  function neutralizeEasify(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var fields = scope.querySelectorAll(
      '.tpo_option-set-wrapper input, .tpo_option-set-wrapper textarea, .tpo_option-set-wrapper select,' +
        '.tpo_option-set-container input, .tpo_option-set-container textarea, .tpo_option-set-container select,' +
        'input[class*="tpo_"], textarea[class*="tpo_"], select[class*="tpo_"]'
    );

    for (var i = 0; i < fields.length; i++) {
      var field = fields[i];
      if (field.closest('product-measurements')) continue;
      field.disabled = true;
      field.required = false;
      field.removeAttribute('name');
      field.removeAttribute('form');
    }

    var wraps = scope.querySelectorAll('.tpo_option-set-wrapper, .tpo_option-set-container');
    for (var j = 0; j < wraps.length; j++) {
      wraps[j].hidden = true;
      wraps[j].style.setProperty('display', 'none', 'important');
    }
  }

  function watchEasify() {
    neutralizeEasify(document);
    if (typeof MutationObserver === 'undefined') return;
    var host = document.querySelector('.product-single__meta') || document.body;
    var queued = false;
    var observer = new MutationObserver(function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        neutralizeEasify(document);
      });
    });
    observer.observe(host, { childList: true, subtree: true });
    document.addEventListener(
      'click',
      function (event) {
        var target = event.target;
        if (
          target &&
          target.closest &&
          target.closest('[data-add-to-cart], .add-to-cart, .sticky-buy-bar, shopify-accelerated-checkout, .shopify-payment-button')
        ) {
          return;
        }
        neutralizeEasify(document);
      },
      true
    );
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchEasify);
  else watchEasify();

  if (typeof customElements === 'undefined' || customElements.get('product-measurements')) return;

  // Punctuation and case are not consistent across an old catalog, so compare on a
  // normalised form: lowercase, alphanumerics only, single-spaced.
  function normalise(value) {
    return String(value == null ? '' : value)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  var ProductMeasurements = function () {};

  ProductMeasurements = class extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.optionIndex = parseInt(this.getAttribute('data-option-index'), 10) || 1;
      this.requireWhenVisible = this.getAttribute('data-require-when-visible') === 'true';
      this.formId = this.getAttribute('data-form-id');

      var raw = this.getAttribute('data-trigger-values');
      try {
        this.triggers = JSON.parse(raw || '[]').map(normalise);
      } catch (e) {
        this.triggers = [];
      }

      this.fieldsWrapper = this.querySelector('[data-measurements-fields]');
      this.inputs = Array.prototype.slice.call(this.querySelectorAll('[data-measurement-input]'));

      // Scope lookups to this product section so a quick-view or a second product
      // section on the same page cannot cross-talk.
      this.scope = this.closest('[data-section-id]') || document;

      this._onChange = this._onChange.bind(this);
      this.scope.addEventListener('change', this._onChange);
      this.scope.addEventListener('variant:change', this._onChange);
      document.addEventListener('variant:change', this._onChange);

      // Easify and other option apps can inject asynchronously and re-render the
      // column; re-evaluate when our own subtree is rebuilt.
      if (typeof MutationObserver !== 'undefined' && this.fieldsWrapper) {
        this._observer = new MutationObserver(
          function () {
            this.inputs = Array.prototype.slice.call(this.querySelectorAll('[data-measurement-input]'));
            this.update(true);
          }.bind(this)
        );
        this._observer.observe(this.fieldsWrapper, { childList: true, subtree: true });
      }

      // Evaluate immediately so a preselected made-to-measure variant is already
      // revealed on load rather than only after the first interaction.
      this.update(true);
    }

    disconnectedCallback() {
      this.scope.removeEventListener('change', this._onChange);
      this.scope.removeEventListener('variant:change', this._onChange);
      document.removeEventListener('variant:change', this._onChange);
      if (this._observer) this._observer.disconnect();
      this._ready = false;
    }

    _onChange() {
      // Impulse updates the checked radio and dispatches in the same tick; read after it settles.
      if (this._raf) cancelAnimationFrame(this._raf);
      this._raf = requestAnimationFrame(
        function () {
          this._raf = null;
          this.update();
        }.bind(this)
      );
    }

    /** The currently selected value of the option that carries the trigger. */
    selectedValue() {
      var selector = '[data-index="option' + this.optionIndex + '"]';

      var radio = this.scope.querySelector('input[type="radio"]' + selector + ':checked');
      if (radio) return radio.value;

      var select = this.scope.querySelector('select' + selector);
      if (select) return select.value;

      // Dropdown pickers in some Impulse configurations only carry the option name.
      var named = this.scope.querySelector('[data-single-option-selector][data-index="' + this.optionIndex + '"]');
      if (named) return named.value;

      return null;
    }

    isTriggered() {
      if (!this.triggers.length) return false;
      return this.triggers.indexOf(normalise(this.selectedValue())) !== -1;
    }

    update(force) {
      var active = this.isTriggered();
      if (!force && active === this._active) return;
      this._active = active;

      if (active) this.removeAttribute('hidden');
      else this.setAttribute('hidden', '');

      // A disabled fieldset bars every descendant from submission in one move; the
      // per-input pass below also handles `required` and clearing.
      if (this.fieldsWrapper && 'disabled' in this.fieldsWrapper) {
        this.fieldsWrapper.disabled = !active;
      }

      this.inputs.forEach(
        function (input) {
          input.disabled = !active;
          if (this.requireWhenVisible) input.required = active;
          if (!active && input.value !== '') input.value = '';
        }.bind(this)
      );

      this.dispatchEvent(
        new CustomEvent('measurements:toggle', { bubbles: true, detail: { visible: active } })
      );
    }
  };

  customElements.define('product-measurements', ProductMeasurements);
})();
